// THE DATE COLUMN OF THE LISTING ROW (Figma "Listing design row" — tasks 3668-28826,
// notes 3717-33937, events 3793-36498).
//
// The three modules share one row but not one stamp: a task says how near its DUE date is in
// words ("Yesterday", "Tomorrow", "Tuesday"), a note says when it was last TOUCHED ("11:35",
// "8 Jun"), and an event says the day it HAPPENS ("12 Jul") — or, when it repeats, the rule
// instead of a day ("Every Mon"). The wording is the whole difference between the three frames,
// so it lives here as plain functions rather than inside the row component: a stamp can be
// checked without mounting a list, and the three modules cannot drift apart.
//
// Everything is `now`-injectable so a test never has to travel in time.

// Dates arrive from the API without a zone marker on some rows; the listings have always read
// those as UTC (see EntityPlaceholders/EntityDate.vue and lib/emailListingRow.js), and reading
// them any other way here would move every row by the user's offset.
export const parseDate = (raw) => {
  if (raw == null || raw === '') return null
  if (raw instanceof Date) return isNaN(raw.getTime()) ? null : raw
  if (typeof raw === 'number') {
    const d = new Date(raw)
    return isNaN(d.getTime()) ? null : d
  }
  if (typeof raw !== 'string') return null
  const s = !raw.endsWith('Z') && !raw.includes('+') ? `${raw}Z` : raw
  const d = new Date(s)
  return isNaN(d.getTime()) ? null : d
}

export const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
export const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
export const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const pad = (n) => String(n).padStart(2, '0')
const toNow = (now) => (now instanceof Date ? now : now ? new Date(now) : new Date())

// Whole CALENDAR days between two instants — not 24-hour blocks. "Tomorrow" is the next date on
// the wall, whether that is 2 hours or 23 hours away.
export const dayDiff = (d, now) => {
  const ref = toNow(now)
  const a = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const b = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate()).getTime()
  return Math.round((a - b) / 86400000)
}

// "22 Sep" — the stamp for anything outside the coming week, spelled the way every other listing
// spells a day (the notes and events stamps below, the mailbox's `emailListingRow`), with the
// two-digit year only when it is not this year's. The frame's own fallback is the US "6/18/26",
// which no other listing in the app uses — the 2026-09-15 parity audit (A-2, busy-my-web#454)
// settled on one spelling across the listings rather than the frame's. Kept under the old name
// so the tasks stamp reads the same function it always did.
export const shortSlashDate = (d, now = new Date()) => {
  const stamp = `${d.getDate()} ${MONTHS[d.getMonth()]}`
  return d.getFullYear() === toNow(now).getFullYear() ? stamp : `${stamp} ${String(d.getFullYear()).slice(-2)}`
}

/**
 * A TASK's due date, in the frame's words.
 *   Yesterday · Today · Tomorrow · a weekday name inside the coming week · 22 Sep
 * Unscheduled (and done / someday, which the listing never stamps) → ''.
 */
export const taskDateLabel = (entity, now = new Date()) => {
  if (!entity) return ''
  if (entity.status === 'done' || entity.status === 'someday') return ''
  const d = parseDate(entity.dueDate)
  if (!d) return ''
  const diff = dayDiff(d, now)
  if (diff === -1) return 'Yesterday'
  if (diff === 0) return 'Today'
  if (diff === 1) return 'Tomorrow'
  // Inside the coming week the day has a name; "next Tuesday" is unambiguous only while it is
  // less than a week away, so day 7 falls through to the numeric stamp.
  if (diff >= 2 && diff <= 6) return WEEKDAYS[d.getDay()]
  return shortSlashDate(d, now)
}

/**
 * A NOTE's stamp: when it last changed. Today is a clock ("11:35"), any other day this year is
 * "8 Jun", and another year keeps its two digits ("8 Jun 25") — a bare "8 Jun" that is really
 * 2025 would be a lie.
 */
export const noteDateLabel = (entity, now = new Date()) => {
  const d = parseDate(entity?.updateDate || entity?.createDate)
  if (!d) return ''
  const ref = toNow(now)
  if (dayDiff(d, ref) === 0) return `${pad(d.getHours())}:${pad(d.getMinutes())}`
  const stamp = `${d.getDate()} ${MONTHS[d.getMonth()]}`
  return d.getFullYear() === ref.getFullYear() ? stamp : `${stamp} ${String(d.getFullYear()).slice(-2)}`
}

// The instant an event starts, whatever the record calls it.
export const eventStart = (entity) =>
  parseDate(entity?.startDateTime || entity?.start || entity?.startDate || entity?.dueDate)

/**
 * How often an event repeats, said the way the frame's first group says it: "Every Mon".
 *
 * Reads the structured rule the calendar service takes (see lib/eventRecurrence.js — frequency 2
 * is weekly, `days` are .NET DayOfWeek ordinals) and the RRULE text a synced Google event may
 * carry instead. '' when the record says nothing about repeating, which is also what a listing
 * whose API does not return the rules will get — the row then falls back to the day stamp rather
 * than inventing a rule.
 */
export const recurrenceLabel = (entity) => {
  const raw = entity?.rules ?? entity?.recurrence ?? entity?.recurrenceRules ?? entity?.rrule
  const list = Array.isArray(raw) ? raw : raw ? [raw] : []
  for (const rule of list) {
    if (!rule) continue
    if (typeof rule === 'string') {
      const label = recurrenceLabelFromText(rule, entity)
      if (label) return label
      continue
    }
    const freq = rule.frequency ?? rule.Frequency
    const days = rule.days ?? rule.Days ?? []
    if (freq === 1 || freq === 'Daily') return 'Every day'
    if (freq === 3 || freq === 'Monthly') return 'Every month'
    if (freq === 4 || freq === 'Yearly') return 'Every year'
    if (freq === 2 || freq === 'Weekly') {
      const set = [...new Set((Array.isArray(days) ? days : []).map(Number))].sort()
      if (set.length === 5 && [1, 2, 3, 4, 5].every((d) => set.includes(d))) return 'Every weekday'
      const day = set.length ? set[0] : eventStart(entity)?.getDay()
      return day == null ? 'Weekly' : `Every ${WEEKDAYS_SHORT[day]}`
    }
    const label = recurrenceLabelFromText(String(rule.rule || rule.rrule || rule.value || ''), entity)
    if (label) return label
  }
  return ''
}

const RRULE_DAYS = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 }

const recurrenceLabelFromText = (text, entity) => {
  const freq = String(text || '').match(/FREQ=([A-Z]+)/i)?.[1]?.toUpperCase()
  if (!freq) return ''
  if (freq === 'DAILY') return 'Every day'
  if (freq === 'MONTHLY') return 'Every month'
  if (freq === 'YEARLY') return 'Every year'
  if (freq !== 'WEEKLY') return ''
  const byDay = String(text).match(/BYDAY=([A-Z,]+)/i)?.[1]?.toUpperCase() || ''
  const codes = byDay.split(',').filter(Boolean)
  const working = ['MO', 'TU', 'WE', 'TH', 'FR']
  if (codes.length === working.length && working.every((d) => codes.includes(d))) return 'Every weekday'
  const day = codes.length ? RRULE_DAYS[codes[0]] : eventStart(entity)?.getDay()
  return day == null ? 'Weekly' : `Every ${WEEKDAYS_SHORT[day]}`
}

/** Does this event repeat at all? (The Events listing groups the repeating ones first.) */
export const isRecurringEvent = (entity) => recurrenceLabel(entity) !== ''

/**
 * An EVENT's stamp: "12 Jul", or the repeat rule ("Every Mon") when the record carries one — the
 * frame's Weekly group has no day, because a weekly event does not happen on one.
 */
export const eventDateLabel = (entity, now = new Date()) => {
  const rule = recurrenceLabel(entity)
  if (rule) return rule
  const d = eventStart(entity)
  if (!d) return ''
  const stamp = `${d.getDate()} ${MONTHS[d.getMonth()]}`
  return d.getFullYear() === toNow(now).getFullYear() ? stamp : `${stamp} ${String(d.getFullYear()).slice(-2)}`
}

/** The stamp for whichever module is drawing the row. */
export const listingDateLabel = (entity, type, now = new Date()) => {
  if (type === 'tasks') return taskDateLabel(entity, now)
  if (type === 'notes') return noteDateLabel(entity, now)
  if (type === 'events') return eventDateLabel(entity, now)
  return ''
}

export default listingDateLabel
